import React, { useCallback } from 'react';
import {
   FieldRenderProps,
   FormItem,
   getFieldErrorMessage,
   Validator
} from '../../form';
import { Label } from '../../../display/label';

export interface BaseFormItemProps<T> {
   name: string;
   inline?: boolean;
   label?: string;
   disableLabelWordWrap?: boolean;
   horizontalFill?: boolean;
   hint?: string;
   helpText?: string;
   required?: boolean;
   maxLabelWidth?: number | string;
   min?: number;
   max?: number;
   minLength?: number;
   maxLength?: number;
   validator?: Validator;
   dataTypeValidator?: Validator;
   triggerOnBlur?: boolean;
   format?: (value: T | undefined, name: string) => string;
   parse?: (value: string | undefined, name: string) => T | undefined;
   isEqual?: (a: T | string, b: T | string) => boolean;
   onClick?: () => void;
   render: (field: FieldRenderProps<T | undefined, HTMLElement, any>) => React.ReactElement;
}

export const BaseFormItem = <T, >(props: React.PropsWithChildren<BaseFormItemProps<T>>) => {

   const { inline, required, hint, maxLabelWidth, helpText, label, disableLabelWordWrap, horizontalFill, render, triggerOnBlur=true } = props;

   const formItemRenderer = useCallback((fieldRenderProps: FieldRenderProps<T | undefined, HTMLElement, any>) => {

      const meta = fieldRenderProps.meta;

      const fieldErrorMessage = getFieldErrorMessage(meta.touched || meta.dirty, meta.error, meta.submitError);
      const extra = fieldErrorMessage ? <span
         className={'uf-form-item-error-message'}>{fieldErrorMessage}</span> : undefined;

      return (
         <Label inline={inline} className={'uf-form-item'}
                showRequiredMark={required} text={label}
                hint={hint} helpText={helpText} extra={extra}
                maxLabelWidth={maxLabelWidth}
                disableLabelWordWrap={disableLabelWordWrap}
                horizontalFill={horizontalFill}
         >
            {render(fieldRenderProps)}
         </Label>
      );
   },[inline, required, hint, maxLabelWidth, helpText, label, disableLabelWordWrap, horizontalFill, render]);

   return (
      <FormItem
         name={props.name}
         inline={props.inline}
         label={props.label}
         hint={props.hint}
         required={props.required}
         min={props.min}
         max={props.max}
         minLength={props.minLength}
         maxLength={props.maxLength}
         validator={props.validator}
         dataTypeValidator={props.dataTypeValidator}
         format={props.format}
         parse={props.parse}
         isEqual={props.isEqual}
         render={formItemRenderer}
         triggerOnBlur={props.triggerOnBlur}
      />
   );
};
